// src/admin/auth/useRole.ts
import { useMemo } from 'react';
import { useAuth } from './useAuth';

export type Capability =
  | 'loans.read' | 'loans.approve' | 'loans.mark_disbursed' | 'loans.record_payment'
  | 'loans.cancel' | 'loans.request_restructure' | 'loans.recommend_default'
  | 'loans.force_fail_disbursement'
  | 'borrowers.read' | 'borrowers.freeze' | 'borrowers.reset_phone' | 'borrowers.add_note'
  | 'kyc.read' | 'kyc.review'
  | 'collections.read' | 'collections.log_contact' | 'collections.assign'
  | 'audit.read'
  | 'admins.read' | 'admins.invite' | 'admins.deactivate' | 'admins.reset_totp' | 'admins.set_readonly' | 'admins.change_role'
  | 'config.read' | 'config.update'
  | 'actions.review';

export function useRole() {
  const { admin } = useAuth();

  return useMemo(() => {
    const role = admin?.role ?? null;
    const isReadonly = admin?.is_readonly ?? false;

    function can(cap: Capability): boolean {
      if (!role) return false;

      // Reads: any admin role can read
      if (cap.endsWith('.read')) return true;

      // No role except super_admin can manage admins or config
      if (cap.startsWith('admins.')) return role === 'super_admin' && !isReadonly;
      if (cap.startsWith('config.')) return role === 'super_admin' && !isReadonly;

      // Action review: super_admin only (second pair of eyes)
      if (cap === 'actions.review') return role === 'super_admin' && !isReadonly;

      // Everything else requires write permission
      if (isReadonly) return false;

      if (cap.startsWith('collections.')) {
        return role === 'super_admin' || role === 'admin' || role === 'collections';
      }
      if (cap.startsWith('kyc.review')) return role === 'super_admin' || role === 'admin';
      if (cap.startsWith('loans.')) return role === 'super_admin' || role === 'admin';
      if (cap.startsWith('borrowers.')) return role === 'super_admin' || role === 'admin';

      return false;
    }

    return { role, isReadonly, can };
  }, [admin?.role, admin?.is_readonly]);
}
